'use client';

import { memo, useCallback, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/navigation';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Divider,
  Fade,
  Slide,
  Paper,
  useMediaQuery,
  useTheme,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  LocalShipping as ShippingIcon,
  Calculate as CalculateIcon,
  Bookmark as TemplateIcon,
  BookmarkBorder as SaveTemplateIcon,
  Delete as DeleteIcon,
  ContentCopy as UseTemplateIcon,
  RestartAlt as ResetIcon,
  Security as SecurityIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  LocalOffer as OfferIcon,
  Inventory2 as BulkIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  addTemplate,
  useTemplate,
  removeTemplate,
} from '../../app/redux/slices/shipmentTemplatesSlice';
import { setEditMode } from '../../app/redux/slices/bookingSlice';
import usePricing from '../../hooks/usePricing';
import { calculateDistanceKm } from '../../utils/pricingHelpers';
import { getCurrentLocationAddress } from './utils/addressUtils';
import { COLORS } from './utils/constants';
import useApiCall from '@/services/api/useApiCall';
import { buildApiConfig } from '@/services/api/apiClient';
import { endPointEnums } from '@/services/api/endpoints';
import SimpleNav from '../SimpleNav';
import { useTranslation } from '@/kit/useTranslation';

// Components
import AddressInput from './components/AddressInput';
import WeightSelector from './components/WeightSelector';
import DimensionsInput from './components/DimensionsInput';
import ParcelTypeSelector from './components/ParcelTypeSelector';
import SafetyGuidelines from './components/SafetyGuidelines';
import BulkBookingPromo from './components/BulkBookingPromo';
import MobilePromoFloat from './components/MobilePromoFloat';
import PriceSummaryCard from './components/PriceSummaryCard';
import UnitsSelector from './components/UnitsSelector';
import { formatApiError } from '@/services/api/apiHelpers';

const PriceEstimator = memo(({ onBookConsignment }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const router = useRouter();
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { calculatePrice, loading: pricingLoading } = usePricing();
  const templates = useSelector((state) => state.shipmentTemplates?.templates || []);

  // Get booking state for edit mode hydration
  const bookingState = useSelector((state) => state.booking);
  const editMode = bookingState?.editMode || false;
  const bookingDetails = bookingState?.details || null;

  // Check if user is a business user (for bulk booking feature)
  const auth = useSelector((state) => state.auth);
  const user = auth?.user;
  // const isAuthenticated = !!auth?.token;
  const isBusinessUser = user?.userType === 'BUSINESS' || user?.user_type === 'BUSINESS';
  const businessSetupCompleted = user?.businessSetupCompleted || user?.business_setup_completed;

  // Form state
  const [origin, setOrigin] = useState('');
  const [originData, setOriginData] = useState(null);
  const [destination, setDestination] = useState('');
  const [destinationData, setDestinationData] = useState(null);

  // debug logging to help track location state on booking/estimate pages
  useEffect(() => {
    console.log('[PriceEstimator] origin state updated', { origin, originData });
  }, [origin, originData]);
  useEffect(() => {
    console.log('[PriceEstimator] destination state updated', { destination, destinationData });
  }, [destination, destinationData]);

  // Hydrate form from Redux when in edit mode
  useEffect(() => {
    if (editMode && bookingDetails) {
      console.log('[PriceEstimator] Hydrating form from Redux edit mode', bookingDetails);

      // Hydrate origin - handle both object and string formats
      if (bookingDetails.origin) {
        if (typeof bookingDetails.origin === 'object' && bookingDetails.origin !== null) {
          // Origin is a full address object from API - use full address for display
          const originFullAddress =
            bookingDetails.origin.savedAddressFullAddress ||
            bookingDetails.origin.fullAddress ||
            bookingDetails.origin.address ||
            bookingDetails.origin.label ||
            '';
          setOrigin(originFullAddress);
          setOriginData(bookingDetails.origin);
          console.log(
            '[PriceEstimator] Restored origin from object:',
            originFullAddress,
            bookingDetails.origin
          );
        } else if (typeof bookingDetails.origin === 'string') {
          // Origin is just a string label
          setOrigin(bookingDetails.origin);
          setOriginData(null);
          console.log('[PriceEstimator] Restored origin from string:', bookingDetails.origin);
        }
      }

      // Hydrate destination - handle both object and string formats
      if (bookingDetails.destination) {
        if (typeof bookingDetails.destination === 'object' && bookingDetails.destination !== null) {
          // Destination is a full address object from API - use full address for display
          const destFullAddress =
            bookingDetails.destination.savedAddressFullAddress ||
            bookingDetails.destination.fullAddress ||
            bookingDetails.destination.address ||
            bookingDetails.destination.label ||
            '';
          setDestination(destFullAddress);
          setDestinationData(bookingDetails.destination);
          console.log(
            '[PriceEstimator] Restored destination from object:',
            destFullAddress,
            bookingDetails.destination
          );
        } else if (typeof bookingDetails.destination === 'string') {
          // Destination is just a string label
          setDestination(bookingDetails.destination);
          setDestinationData(null);
          console.log(
            '[PriceEstimator] Restored destination from string:',
            bookingDetails.destination
          );
        }
      }

      // Hydrate parcel details
      if (bookingDetails.parcelWeight) setParcelWeight(bookingDetails.parcelWeight);
      if (bookingDetails.parcelType && Array.isArray(bookingDetails.parcelType)) {
        setParcelType(bookingDetails.parcelType);
      }

      // Hydrate volume
      if (bookingDetails.volume && typeof bookingDetails.volume === 'object') {
        setVolume(bookingDetails.volume);
      }

      // Hydrate units for B2B bulk booking
      if (bookingDetails.units && typeof bookingDetails.units === 'number') {
        setParcelUnits(bookingDetails.units);
      }

      // Hydrate calculated price from existing booking
      if (bookingDetails.breakdown || bookingDetails.estimatedPrice || bookingDetails.distance) {
        setCalculatedPrice({
          finalPrice: bookingDetails.estimatedPrice || bookingDetails.price,
          volume: bookingDetails.parcelVolume,
          distance: bookingDetails.distance,
          breakdown: bookingDetails.breakdown,
        });
      }

      // Clear edit mode flag after hydration
      dispatch(setEditMode(false));
    }
  }, [editMode, bookingDetails, dispatch]);

  const [parcelWeight, setParcelWeight] = useState(1);
  const [parcelType, setParcelType] = useState([]);
  const [volume, setVolume] = useState({ length: '', breadth: '', height: '' });
  const [parcelUnits, setParcelUnits] = useState(1); // Number of boxes for bulk booking (B2B)

  // UI state
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [error, setError] = useState('');
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);

  // Template state
  const [templateMenuAnchor, setTemplateMenuAnchor] = useState(null);
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');

  // Template handlers
  const handleOpenTemplateMenu = (event) => {
    setTemplateMenuAnchor(event.currentTarget);
  };

  const handleCloseTemplateMenu = () => {
    setTemplateMenuAnchor(null);
  };

  const handleSelectTemplate = (template) => {
    // If template stores origin/destination as objects (preferred), set both label and data
    if (template.origin && typeof template.origin === 'object') {
      setOrigin(
        template.origin.label || template.origin.address || template.origin.formatted_address || ''
      );
      setOriginData(template.origin);
    } else {
      setOrigin(template.origin || '');
      setOriginData(null);
    }

    if (template.destination && typeof template.destination === 'object') {
      setDestination(
        template.destination.label ||
          template.destination.address ||
          template.destination.formatted_address ||
          ''
      );
      setDestinationData(template.destination);
    } else {
      setDestination(template.destination || '');
      setDestinationData(null);
    }
    setParcelWeight(template.parcelWeight || 1);
    setParcelType(template.parcelType || []);
    setVolume(template.volume || { length: '', breadth: '', height: '' });
    setCalculatedPrice(null);
    dispatch(useTemplate(template.id));
    handleCloseTemplateMenu();
  };

  const handleDeleteTemplate = (e, templateId) => {
    e.stopPropagation();
    dispatch(removeTemplate(templateId));
  };

  const handleOpenSaveDialog = () => {
    if (!origin || !destination) {
      setError(t('estimate.errors.enterAddresses'));
      return;
    }
    setTemplateName(`${origin.slice(0, 20)} → ${destination.slice(0, 20)}`);
    setSaveTemplateDialogOpen(true);
  };

  const { makeApiCall } = useApiCall();

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return;
    setSaveTemplateDialogOpen(false);

    try {
      const config = buildApiConfig(endPointEnums.createShipmentTemplate);
      const payload = {
        name: templateName,
        origin: originData ? originData : origin,
        destination: destinationData ? destinationData : destination,
        parcelWeight,
        parcelType,
        volume,
        receiverName: '',
        receiverPhone: '',
      };

      const response = await makeApiCall({ ...config, data: payload });
      if (response?.success) {
        // Server returns created template - add to redux
        dispatch(addTemplate(response.data));
      } else {
        // Fallback to adding locally
        dispatch(addTemplate(payload));
      }
    } catch (err) {
      console.error('[PriceEstimator] Save template failed', err);
      // fallback
      dispatch(
        addTemplate({
          name: templateName,
          origin: originData ? originData : origin,
          destination: destinationData ? destinationData : destination,
          parcelWeight,
          parcelType,
          volume,
        })
      );
    }

    setTemplateName('');
  };

  // Reset all form fields
  const handleReset = useCallback(() => {
    setOrigin('');
    setOriginData(null);
    setDestination('');
    setDestinationData(null);
    setParcelWeight(1);
    setParcelType([]);
    setVolume({ length: '', breadth: '', height: '' });
    setCalculatedPrice(null);
    setError('');
  }, []);

  // Get current location
  const handleGetCurrentLocation = useCallback(async () => {
    setLoadingLocation(true);
    setError('');

    try {
      const addressObj = await getCurrentLocationAddress(
        process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY
      );
      // addressObj is an object: { label, address, latitude, longitude }
      if (addressObj && typeof addressObj === 'object') {
        const label =
          addressObj.label ||
          addressObj.address ||
          `${addressObj.latitude}, ${addressObj.longitude}`;
        setOrigin(label);
        setOriginData(addressObj);
      } else if (typeof addressObj === 'string') {
        setOrigin(addressObj);
        setOriginData(null);
      }
      setCalculatedPrice(null);
    } catch (err) {
      setError(t('estimate.errors.locationFailed'));
      console.error('Location error:', err);
    } finally {
      setLoadingLocation(false);
    }
  }, []);

  // Calculate price
  const handleCalculatePrice = useCallback(async () => {
    setError('');

    // Validation
    if (!origin.trim()) {
      setError(t('estimate.errors.enterPickup'));
      return;
    }
    if (!destination.trim()) {
      setError(t('estimate.errors.enterDelivery'));
      return;
    }
    if (origin === destination) {
      setError(t('estimate.errors.sameAddress'));
      return;
    }
    if (parcelWeight < 0.1 || parcelWeight > 1000) {
      setError(t('estimate.errors.weightRange'));
      return;
    }
    if (parcelType.length === 0) {
      setError(t('estimate.errors.selectParcelType'));
      return;
    }

    try {
      // Need lat/lng for distance calculation
      let distance = 0;

      // Check if we have valid coordinates from saved addresses or address selection
      const hasOriginCoords =
        originData &&
        typeof originData.latitude === 'number' &&
        typeof originData.longitude === 'number';
      const hasDestCoords =
        destinationData &&
        typeof destinationData.latitude === 'number' &&
        typeof destinationData.longitude === 'number';

      if (hasOriginCoords && hasDestCoords) {
        // Use the coordinates from address selection
        distance = await calculateDistanceKm(
          { lat: originData.latitude, lng: originData.longitude },
          { lat: destinationData.latitude, lng: destinationData.longitude }
        );
      } else {
        // Fallback: try to geocode the addresses
        const geocodeAddress = async (address) => {
          try {
            const response = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
                address
              )}&key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}`
            );
            const data = await response.json();
            if (data.results && data.results[0]) {
              const location = data.results[0].geometry.location;
              return { lat: location.lat, lng: location.lng };
            }
            return null;
          } catch (err) {
            console.error('Geocode error:', err);
            return null;
          }
        };

        const originCoords = await geocodeAddress(origin);
        const destCoords = await geocodeAddress(destination);

        if (originCoords && destCoords) {
          distance = await calculateDistanceKm(originCoords, destCoords);
        }
      }

      if (!distance || distance === 0) {
        setError(t('estimate.errors.distanceFailed'));
        return;
      }

      // Calculate price (include units for B2B bulk orders)
      const service = parcelType[0]?.toLowerCase() || 'standard';
      const result = await calculatePrice({
        distance,
        weight: parcelWeight,
        service,
        units: isBusinessUser ? parcelUnits : 1,
      });

      if (result && result.finalPrice) {
        const priceData = result;
        setCalculatedPrice({
          ...priceData,
          distance,
          volume:
            volume.length && volume.breadth && volume.height
              ? (parseFloat(volume.length) *
                  parseFloat(volume.breadth) *
                  parseFloat(volume.height)) /
                1000000
              : null,
        });
      } else {
        console.error('Price calculation failed:', result);
        setError(t('estimate.errors.priceFailed'));
      }
    } catch (err) {
      console.error('[PriceEstimator] Calculate price error:', err);
      setError(err.message || t('estimate.errors.priceError'));
    }
  }, [
    origin,
    destination,
    originData,
    destinationData,
    parcelWeight,
    parcelType,
    volume,
    calculatePrice,
  ]);

  // Handle type toggle
  const handleTypeToggle = useCallback((typeLabel) => {
    setParcelType((prev) =>
      prev.includes(typeLabel) ? prev.filter((t) => t !== typeLabel) : [...prev, typeLabel]
    );
    setCalculatedPrice(null);
  }, []);

  // Handle volume change
  const handleVolumeChange = useCallback((newVolume) => {
    setVolume(newVolume);
    setCalculatedPrice(null);
  }, []);

  // Handle weight change
  const handleWeightChange = useCallback((newWeight) => {
    setParcelWeight(newWeight);
    setCalculatedPrice(null);
  }, []);

  // Handle units change (for B2B bulk booking)
  const handleUnitsChange = useCallback((newUnits) => {
    setParcelUnits(newUnits);
    setCalculatedPrice(null);
  }, []);

  // Handle book consignment
  const handleBookConsignment = useCallback(async () => {
    if (!calculatedPrice) {
      setError(t('estimate.errors.calculateFirst'));
      return;
    }

    setBookingLoading(true);

    const bookingData = {
      origin: originData ? originData : { label: origin },
      destination: destinationData ? destinationData : { label: destination },
      parcelWeight,
      parcelType,
      // Keep original dimensions as well as computed CBM volume
      volume: volume, // dimensions object: { length, breadth, height }
      parcelVolume: calculatedPrice.volume,
      // price should be mapped to 'price' expected by order API
      price: calculatedPrice.finalPrice,
      estimatedPrice: calculatedPrice.finalPrice,
      breakdown: calculatedPrice.breakdown,
      distance: calculatedPrice.distance,
      // B2B bulk booking: number of boxes/parcels with same origin-destination
      units: isBusinessUser ? parcelUnits : 1,
      isBusinessUser,
    };

    if (onBookConsignment) {
      try {
        await onBookConsignment(bookingData);
      } finally {
        setBookingLoading(false);
      }
    } else {
      setBookingLoading(false);
    }
  }, [
    calculatedPrice,
    origin,
    destination,
    originData,
    destinationData,
    parcelWeight,
    parcelType,
    volume,
    parcelUnits,
    isBusinessUser,
    onBookConsignment,
  ]);

  const formatPrice = (p) => {
    if (!p) return '₹0';
    return `₹${Math.round(p)}`;
  };

  const isCalculateDisabled =
    pricingLoading ||
    !origin ||
    !destination ||
    parcelType.length === 0 ||
    !parcelWeight ||
    !volume.length ||
    !volume.breadth ||
    !volume.height;

  // Calculate form completion progress (now 5 required fields)
  const getFormProgress = () => {
    let completed = 0;
    if (origin) completed++;
    if (destination) completed++;
    if (parcelWeight) completed++;
    if (parcelType.length > 0) completed++;
    if (volume.length && volume.breadth && volume.height) completed++;
    return (completed / 5) * 100;
  };

  const formProgress = getFormProgress();

  // Check if user is authenticated
  const isAuthenticated = useSelector((state) => state.auth?.isLoggedIn);

  // Desktop price summary component

  return (
    <Box
      sx={{
        minHeight: isDesktop ? 'auto' : '90vh',
        background: isDesktop
          ? `linear-gradient(135deg, ${COLORS.surface} 0%, ${COLORS.white} 50%, ${COLORS.surface} 100%)`
          : COLORS.background,
        pb: isDesktop ? 4 : calculatedPrice ? 22 : 14,
        pt: isDesktop ? (isAuthenticated ? 4 : 10) : isAuthenticated ? 0 : 7, // Add top padding for SimpleNav when not authenticated
      }}
    >
      {/* Simple Navigation for Non-Authenticated Users ONLY */}
      {!isAuthenticated && <SimpleNav />}

      <Container
        maxWidth={isLargeDesktop ? 'xl' : 'lg'}
        sx={{ pt: isDesktop ? 0 : 1, px: { xs: 2, md: 4, lg: 6 } }}
      >
        {/* Business User Bulk Booking Alert */}
        {/* {isBusinessUser && businessSetupCompleted && (
          <Alert
            severity="info"
            icon={<BulkIcon />}
            sx={{
              mb: 3,
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              '& .MuiAlert-message': { width: '100%' },
            }}
            action={
              <Button
                color="primary"
                size="small"
                variant="contained"
                onClick={() => router.push('/bulk-booking')}
                startIcon={<BulkIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                {t('estimate.bulk.goToBulk')}
              </Button>
            }
          >
            <strong>{t('estimate.bulk.needMultiple')}</strong> {t('estimate.bulk.needMultipleDesc')}
          </Alert>
        )} */}

        {/* Desktop Header */}
        {isDesktop && (
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {/* <ShippingIcon sx={{ fontSize: 32, color: COLORS.primary }} /> */}
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: COLORS.textPrimary,
                    letterSpacing: '-1px',
                  }}
                >
                  {t('estimate.title')}
                </Typography>
              </Box>

              {/* Templates & Reset Buttons */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {(origin || destination || parcelWeight > 1 || parcelType.length > 0) && (
                  <Tooltip title={t('estimate.tooltips.clearAll')}>
                    <Button
                      variant="outlined"
                      startIcon={<ResetIcon />}
                      onClick={handleReset}
                      sx={{
                        textTransform: 'none',
                        borderColor: COLORS.border,
                        color: COLORS.textSecondary,
                        '&:hover': {
                          borderColor: COLORS.accent,
                          bgcolor: `${COLORS.accent}08`,
                          color: COLORS.accent,
                        },
                      }}
                    >
                      {t('estimate.actions.reset')}
                    </Button>
                  </Tooltip>
                )}
                {templates.length > 0 && (
                  <Tooltip title={t('estimate.tooltips.loadTemplates')}>
                    <Button
                      variant="outlined"
                      startIcon={<TemplateIcon />}
                      onClick={handleOpenTemplateMenu}
                      sx={{
                        borderColor: COLORS.border,
                        color: COLORS.textPrimary,
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          borderColor: COLORS.primary,
                          backgroundColor: `${COLORS.primary}05`,
                        },
                      }}
                    >
                      Templates ({templates.length})
                    </Button>
                  </Tooltip>
                )}
                <Tooltip title={t('estimate.tooltips.saveAsTemplate')}>
                  <Button
                    variant="outlined"
                    startIcon={<SaveTemplateIcon />}
                    onClick={handleOpenSaveDialog}
                    disabled={!origin || !destination}
                    sx={{
                      borderColor: COLORS.border,
                      color: COLORS.textPrimary,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: COLORS.primary,
                        backgroundColor: `${COLORS.primary}05`,
                      },
                      '&:disabled': {
                        borderColor: COLORS.border,
                        color: COLORS.textTertiary,
                      },
                    }}
                  >
                    {t('estimate.templates.saveTemplate')}
                  </Button>
                </Tooltip>
              </Box>
            </Box>
            <Typography variant="body1" sx={{ color: COLORS.textSecondary, maxWidth: 500 }}>
              {t('estimate.subtitle')}
            </Typography>
          </Box>
        )}

        {/* Mobile Header */}
        {!isDesktop && (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: COLORS.primary,
                  fontSize: '1.5rem',
                  letterSpacing: '-0.5px',
                }}
              >
                {t('estimate.title')}
              </Typography>

              {/* Mobile Templates Button */}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {(origin || destination || parcelWeight > 1 || parcelType.length > 0) && (
                  <IconButton
                    onClick={handleReset}
                    sx={{ color: COLORS.textSecondary }}
                    size="small"
                    aria-label={t('estimate.tooltips.resetForm')}
                  >
                    <ResetIcon />
                  </IconButton>
                )}
                {templates.length > 0 && (
                  <IconButton
                    onClick={handleOpenTemplateMenu}
                    sx={{ color: COLORS.primary }}
                    size="small"
                    aria-label={t('estimate.tooltips.openTemplates')}
                  >
                    <TemplateIcon />
                  </IconButton>
                )}
                <IconButton
                  onClick={handleOpenSaveDialog}
                  disabled={!origin || !destination}
                  sx={{
                    color: origin && destination ? COLORS.primary : COLORS.textTertiary,
                  }}
                  size="small"
                  aria-label={t('estimate.tooltips.saveTemplate')}
                >
                  <SaveTemplateIcon />
                </IconButton>
              </Box>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: COLORS.textSecondary,
                fontSize: '0.875rem',
                mt: 0.5,
              }}
            >
              {t('estimate.subtitleMobile')}
            </Typography>
          </Box>
        )}

        {/* Recent Shipments Quick Select - Desktop Only */}
        {isDesktop && templates.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: COLORS.textSecondary,
                mb: 2,
                fontSize: '0.8rem',
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}
            >
              {t('estimate.templates.recentShipments')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                overflowX: 'auto',
                pb: 1,
                '&::-webkit-scrollbar': { height: 4 },
                '&::-webkit-scrollbar-thumb': {
                  background: COLORS.border,
                  borderRadius: 2,
                },
              }}
            >
              {templates.slice(0, 5).map((template) => (
                <Paper
                  key={template.id}
                  elevation={0}
                  onClick={() => handleSelectTemplate(template)}
                  sx={{
                    p: 2,
                    minWidth: 220,
                    borderRadius: 2.5,
                    border: `1px solid ${COLORS.border}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    background: COLORS.white,
                    '&:hover': {
                      borderColor: COLORS.accent,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 16px rgba(16, 185, 129, 0.12)',
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: COLORS.textPrimary,
                      mb: 1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {template.name || t('estimate.templates.savedShipment')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 0.5,
                    }}
                  >
                    <Typography sx={{ color: COLORS.accent, fontSize: '0.65rem' }}>●</Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: COLORS.textSecondary,
                        fontSize: '0.7rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 180,
                      }}
                    >
                      {typeof template.origin === 'string'
                        ? template.origin?.split(',')[0] || 'Pickup'
                        : template.origin?.label ||
                          template.origin?.address ||
                          template.origin?.formatted_address ||
                          t('estimate.templates.pickup')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ color: COLORS.primary, fontSize: '0.65rem' }}>●</Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: COLORS.textSecondary,
                        fontSize: '0.7rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 180,
                      }}
                    >
                      {typeof template.destination === 'string'
                        ? template.destination?.split(',')[0] || 'Drop-off'
                        : template.destination?.label ||
                          template.destination?.address ||
                          template.destination?.formatted_address ||
                          t('estimate.templates.dropOff')}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      mt: 1.5,
                      display: 'flex',
                      gap: 0.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    {template.parcelWeight && (
                      <Chip
                        label={
                          template.parcelWeight < 1
                            ? `${(template.parcelWeight * 1000).toFixed(0)}g`
                            : `${template.parcelWeight}kg`
                        }
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          bgcolor: COLORS.surface,
                          color: COLORS.textPrimary,
                        }}
                      />
                    )}
                    {template.parcelType?.slice(0, 1).map((type) => (
                      <Chip
                        key={type}
                        label={type}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          bgcolor: COLORS.accentLight || '#d1fae5',
                          color: COLORS.accent,
                        }}
                      />
                    ))}
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        {/* Error Message */}
        {error && isDesktop && (
          <Fade in>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: '#fee',
                border: `1px solid ${COLORS.danger}`,
                mb: 3,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: COLORS.danger,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                }}
              >
                {formatApiError(error)}
              </Typography>
            </Box>
          </Fade>
        )}

        {/* Main Content - Responsive Grid */}
        {isDesktop ? (
          <Grid container spacing={4}>
            {/* Left Column - Form */}
            <Grid item xs={12} md={6} lg={4} width={'50%'}>
              <Stack spacing={3}>
                {/* Addresses Section Card */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: `1px solid ${COLORS.border}`,
                    background: COLORS.white,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: COLORS.accent,
                      boxShadow: '0 4px 20px rgba(16, 185, 129, 0.08)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {/* <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        background: origin && destination ? COLORS.accent : COLORS.surface,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Typography sx={{ fontSize: '1.1rem' }}>📍</Typography>
                    </Box> */}
                    {/* <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.textPrimary }}>
                        Pickup & Delivery
                      </Typography>
                      <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>
                        Enter your pickup and drop-off locations
                      </Typography>
                    </Box> */}
                    {origin && destination && (
                      <Chip
                        label={t('estimate.actions.complete')}
                        size="small"
                        sx={{
                          ml: 'auto',
                          bgcolor: COLORS.accentLight || '#d1fae5',
                          color: COLORS.accent,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    )}
                  </Box>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} lg={6}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: COLORS.textPrimary,
                          mb: 1,
                        }}
                      >
                        {t('estimate.address.pickupLocation')}
                      </Typography>
                      <AddressInput
                        label={t('estimate.address.enterPickupAddress')}
                        value={origin}
                        onChange={(v, data) => {
                          setOrigin(v);
                          // clear previous coordinates when user types or removes selection
                          setOriginData(data || null);
                          setCalculatedPrice(null);
                        }}
                        onUseCurrent={loadingLocation ? null : handleGetCurrentLocation}
                      />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: COLORS.textPrimary,
                          mb: 1,
                        }}
                      >
                        {t('estimate.address.dropOffLocation')}
                      </Typography>
                      <AddressInput
                        label={t('estimate.address.enterDeliveryAddress')}
                        value={destination}
                        onChange={(v, data) => {
                          setDestination(v);
                          setDestinationData(data || null);
                          setCalculatedPrice(null);
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                {/* Package Details Card */}
                <Paper
                  elevation={0}
                  sx={{
                    px: 3,
                    pb: 2,
                    borderRadius: 3,
                    border: `1px solid ${COLORS.border}`,
                    background: COLORS.white,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: COLORS.accent,
                      boxShadow: '0 4px 20px rgba(16, 185, 129, 0.08)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      mb: 2.5,
                    }}
                  >
                    {/* <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        background: parcelWeight ? COLORS.surface : COLORS.accent,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Typography sx={{ fontSize: '1.1rem' }}>📦</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.textPrimary }}>
                        Package Details
                      </Typography>
                      <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>
                        Specify weight and dimensions
                      </Typography>
                    </Box> */}
                    {/* {parcelWeight && (
                      <Chip
                        label={parcelWeight < 1 ? `${(parcelWeight * 1000).toFixed(0)}g` : `${parcelWeight}kg`}
                        size="small"
                        sx={{
                          ml: 'auto',
                          bgcolor: COLORS.surface,
                          color: COLORS.textPrimary,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    )} */}
                  </Box>
                  <Grid container direction={'row'} spacing={2}>
                    {/* Weight & Dimensions Side by Side */}
                    <Grid item xs={12} lg={6}>
                      <WeightSelector value={parcelWeight} onChange={handleWeightChange} />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                      <DimensionsInput volume={volume} onChange={handleVolumeChange} />
                    </Grid>

                    {/* Business User Bulk Booking: Number of Boxes */}
                    {isBusinessUser && (
                      <Grid item xs={12} lg={12}>
                        <UnitsSelector
                          value={parcelUnits}
                          onChange={handleUnitsChange}
                          isDesktop={isDesktop}
                        />
                      </Grid>
                    )}

                    {/* Offers Panel - Large Desktop Only */}
                    {/* {isLargeDesktop && (
                      <Grid item xs={12} lg={3}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                            border: '1px solid #fbbf24',
                            height: '100%',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                            <OfferIcon sx={{ fontSize: 18, color: '#d97706' }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#92400e' }}>
                              Special Offer
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: '#78350f', fontWeight: 600, display: 'block', mb: 0.8 }}>
                            Save up to 20%
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#78350f', fontSize: '0.7rem', lineHeight: 1.5 }}>
                            First-time booking discount on packages under 5kg
                          </Typography>
                        </Paper>
                      </Grid>
                    )} */}
                  </Grid>
                </Paper>

                {/* Parcel Type + Safety Guidelines - Side by Side */}
                <Grid container spacing={2.5}>
                  {/* Left: Parcel Type Card */}
                  <Grid item xs={12} lg={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        px: 3,
                        pt: 2.5,
                        pb: 2.5,
                        borderRadius: 3,
                        border: `1px solid ${COLORS.border}`,
                        background: parcelType.length > 0 ? `${COLORS.accent}05` : COLORS.white,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: COLORS.accent,
                          boxShadow: '0 4px 20px rgba(16, 185, 129, 0.08)',
                        },
                        height: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 2,
                        }}
                      >
                        {/* <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.textPrimary }}>
                          What are you sending?
                        </Typography> */}
                        {parcelType.length > 0 && (
                          <Chip
                            label={`${parcelType.join(', ')}`}
                            size="large"
                            sx={{
                              bgcolor: COLORS.accent,
                              color: COLORS.white,
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              // maxWidth: '40%',
                            }}
                          />
                        )}
                      </Box>
                      <ParcelTypeSelector selectedTypes={parcelType} onToggle={handleTypeToggle} />
                    </Paper>
                  </Grid>

                  {/* Right: Safety Guidelines Card */}
                  <Grid item xs={12} lg={6}>
                    <SafetyGuidelines isDesktop={isDesktop} />
                  </Grid>
                </Grid>
              </Stack>
            </Grid>

            {/* Right Column - Price Summary */}
            <Grid item xs={12} md={5} lg={6} width={'45%'}>
              <PriceSummaryCard
                calculatedPrice={calculatedPrice}
                formProgress={formProgress}
                isDesktop={isDesktop}
                handleCalculatePrice={handleCalculatePrice}
                handleBookConsignment={handleBookConsignment}
                parcelType={parcelType}
                parcelWeight={parcelWeight}
                origin={origin}
                destination={destination}
                pricingLoading={pricingLoading}
                bookingLoading={bookingLoading}
                isCalculateDisabled={isCalculateDisabled}
              />
              {/* BulkBookingPromo removed - using yellow banner on booking page instead */}
            </Grid>
          </Grid>
        ) : (
          /* Mobile Layout - Original Stack */
          <Box
            sx={{
              backgroundColor: COLORS.white,
              borderRadius: 3,
              p: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              pb: calculatedPrice ? (isAuthenticated ? 32 : 28) : isAuthenticated ? 15 : 10, // Padding to account for sticky button
              transition: 'padding 0.3s ease',
            }}
          >
            <Stack spacing={2}>
              {/* Addresses */}
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: COLORS.textPrimary,
                    mb: 1.5,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                  }}
                >
                  {t('estimate.address.pickupLocation')}
                </Typography>
                <AddressInput
                  label={t('estimate.address.enterPickupAddress')}
                  value={origin}
                  onChange={(v, data) => {
                    setOrigin(v);
                    if (data) setOriginData(data);
                    setCalculatedPrice(null);
                  }}
                  onUseCurrent={loadingLocation ? null : handleGetCurrentLocation}
                />
              </Box>

              <Divider sx={{ borderColor: COLORS.border }} />

              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: COLORS.textPrimary,
                    mb: 1.5,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                  }}
                >
                  {t('estimate.address.dropOffLocation')}
                </Typography>
                <AddressInput
                  label={t('estimate.address.enterDeliveryAddress')}
                  value={destination}
                  onChange={(v, data) => {
                    setDestination(v);
                    if (data) setDestinationData(data);
                    setCalculatedPrice(null);
                  }}
                />
              </Box>

              <Divider sx={{ borderColor: COLORS.border }} />

              {/* Package Details */}
              <WeightSelector value={parcelWeight} onChange={handleWeightChange} />

              <DimensionsInput volume={volume} onChange={handleVolumeChange} />

              {/* Business User Bulk Booking: Number of Boxes (Mobile) */}
              {isBusinessUser && (
                <UnitsSelector value={parcelUnits} onChange={handleUnitsChange} isDesktop={false} />
              )}

              <Divider sx={{ borderColor: COLORS.border }} />

              {/* Parcel Type */}
              <ParcelTypeSelector selectedTypes={parcelType} onToggle={handleTypeToggle} />
            </Stack>
          </Box>
        )}
      </Container>

      {/* Sticky Calculate Button - Mobile Only, Above BottomNav */}
      {!isDesktop && (
        <Box
          sx={{
            position: 'fixed',
            bottom: isAuthenticated ? 70 : 30, // Height of BottomNav
            left: 0,
            right: 0,
            backgroundColor: COLORS.white,
            borderTop: `1px solid ${COLORS.border}`,
            boxShadow: '0 -4px 12px rgba(0,0,0,0.05)',
            zIndex: 900, // Below BottomNav but above content
          }}
        >
          <Container maxWidth="sm" sx={{ px: 2, py: 2 }}>
            {calculatedPrice ? (
              <Slide direction="up" in={!!calculatedPrice}>
                <Box sx={{ position: 'relative' }}>
                  {/* Close Button for Price Summary */}
                  <IconButton
                    size="small"
                    onClick={() => setCalculatedPrice(null)}
                    sx={{
                      position: 'absolute',
                      right: -8,
                      top: -8,
                      bgcolor: 'rgba(0,0,0,0.04)',
                      zIndex: 10,
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16, color: COLORS.textSecondary }} />
                  </IconButton>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1.5,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: COLORS.textSecondary,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                        }}
                      >
                        {t('estimate.summary.estimatedPrice')}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 900,
                          color: COLORS.primary,
                          fontSize: '2rem',
                          letterSpacing: '-1px',
                        }}
                      >
                        {formatPrice(calculatedPrice.finalPrice)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: COLORS.textSecondary,
                          fontSize: '0.7rem',
                          display: 'block',
                        }}
                      >
                        {calculatedPrice.distance.toFixed(1)} km •{' '}
                        {parcelWeight < 1
                          ? `${(parcelWeight * 1000).toFixed(0)}g`
                          : `${parcelWeight}kg`}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: COLORS.textSecondary,
                          fontSize: '0.7rem',
                          display: 'block',
                        }}
                      >
                        {parcelType.join(', ')}
                      </Typography>
                    </Box>
                  </Box>
                  <Stack spacing={1}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleCalculatePrice}
                      disabled={pricingLoading}
                      startIcon={
                        pricingLoading ? <CircularProgress size={18} color="inherit" /> : null
                      }
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        borderColor: COLORS.primary,
                        color: COLORS.primary,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        letterSpacing: '0.5px',
                        '&:hover': {
                          borderColor: COLORS.primary,
                          backgroundColor: `${COLORS.primary}05`,
                        },
                      }}
                    >
                      {pricingLoading
                        ? t('estimate.summary.recalculating')
                        : t('estimate.summary.recalculatePrice')}
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleBookConsignment}
                      disabled={bookingLoading}
                      endIcon={
                        bookingLoading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <ArrowForwardIcon />
                        )
                      }
                      sx={{
                        py: 1.75,
                        borderRadius: 2,
                        background: COLORS.primary,
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        boxShadow: 'none',
                        '&:hover': {
                          background: COLORS.primaryLight,
                          boxShadow: 'none',
                        },
                      }}
                    >
                      {bookingLoading
                        ? t('estimate.summary.processing')
                        : t('estimate.summary.proceedToBooking')}
                    </Button>
                  </Stack>
                </Box>
              </Slide>
            ) : (
              <Box>
                {error && (
                  <Box
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: 1.5,
                      backgroundColor: '#fee',
                      border: `1px solid ${COLORS.danger}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: COLORS.danger,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    >
                      {formatApiError(error)}
                    </Typography>
                  </Box>
                )}
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleCalculatePrice}
                  disabled={isCalculateDisabled || pricingLoading}
                  endIcon={pricingLoading ? null : <ArrowForwardIcon />}
                  startIcon={pricingLoading ? <CircularProgress size={20} color="inherit" /> : null}
                  sx={{
                    py: 1.75,
                    borderRadius: 2,
                    background: COLORS.primary,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    boxShadow: 'none',
                    '&:hover': {
                      background: COLORS.primaryLight,
                      boxShadow: 'none',
                    },
                    '&:disabled': {
                      background: COLORS.border,
                      color: COLORS.textTertiary,
                    },
                  }}
                >
                  {pricingLoading
                    ? t('estimate.summary.calculating')
                    : t('estimate.summary.seePrice')}
                </Button>
              </Box>
            )}
          </Container>
        </Box>
      )}

      {/* Templates Menu */}
      <Menu
        anchorEl={templateMenuAnchor}
        open={Boolean(templateMenuAnchor)}
        onClose={handleCloseTemplateMenu}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 450,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            borderRadius: 2,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${COLORS.border}` }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.textPrimary }}>
            📋 {t('estimate.templates.title')}
          </Typography>
          <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>
            {t('estimate.templates.description')}
          </Typography>
        </Box>

        {templates.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
              {t('estimate.templates.noTemplates')}
              <br />
              {t('estimate.templates.noTemplatesHint')}
            </Typography>
          </Box>
        ) : (
          templates.map((template) => (
            <MenuItem
              key={template.id}
              onClick={() => handleSelectTemplate(template)}
              sx={{
                py: 1.5,
                borderBottom: `1px solid ${COLORS.border}`,
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <ListItemIcon>
                <UseTemplateIcon sx={{ color: COLORS.primary, fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: COLORS.textPrimary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {template.name ||
                      `${template.origin?.slice(0, 15)}... → ${template.destination?.slice(0, 15)}...`}
                  </Typography>
                }
                secondary={
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.5,
                      mt: 0.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Chip
                      size="small"
                      label={`${template.parcelWeight}kg`}
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                    {template.parcelType?.slice(0, 2).map((type) => (
                      <Chip
                        key={type}
                        size="small"
                        label={type}
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                    ))}
                    {template.lastUsed && (
                      <Typography variant="caption" sx={{ color: COLORS.textTertiary, ml: 0.5 }}>
                        Used {new Date(template.lastUsed).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                }
              />
              <IconButton
                size="small"
                onClick={(e) => handleDeleteTemplate(e, template.id)}
                sx={{ ml: 1, color: COLORS.danger }}
                aria-label={t('estimate.tooltips.deleteTemplate')}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </MenuItem>
          ))
        )}
      </Menu>

      {/* Save Template Dialog */}
      <Dialog
        open={saveTemplateDialogOpen}
        onClose={() => setSaveTemplateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          💾 {t('estimate.templates.saveTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: COLORS.textSecondary, mb: 2 }}>
            {t('estimate.templates.saveDescription')}
          </Typography>
          <MuiTextField
            autoFocus
            fullWidth
            label={t('estimate.templates.templateName')}
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder={t('estimate.templates.templatePlaceholder')}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: COLORS.surface,
              borderRadius: 2,
            }}
          >
            <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600 }}>
              {t('estimate.templates.willSave')}
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ color: COLORS.textPrimary }}>
                📍 {t('estimate.templates.from', { address: origin?.slice(0, 40) + '...' })}
              </Typography>
              <Typography variant="caption" sx={{ color: COLORS.textPrimary }}>
                📍 {t('estimate.templates.to', { address: destination?.slice(0, 40) + '...' })}
              </Typography>
              <Typography variant="caption" sx={{ color: COLORS.textPrimary }}>
                ⚖️{' '}
                {t('estimate.templates.weightAndTypes', {
                  weight: parcelWeight,
                  types: parcelType.join(', ') || 'None',
                })}
              </Typography>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setSaveTemplateDialogOpen(false)}
            sx={{ color: COLORS.textSecondary, textTransform: 'none' }}
          >
            {t('estimate.actions.cancel')}
          </Button>
          <Button
            onClick={handleSaveTemplate}
            variant="contained"
            disabled={!templateName.trim()}
            sx={{
              background: COLORS.primary,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              '&:hover': {
                background: COLORS.primaryLight,
              },
            }}
          >
            {t('estimate.templates.saveTemplate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mobile Floating Promo Button */}
      {!isDesktop && <MobilePromoFloat />}
    </Box>
  );
});

PriceEstimator.displayName = 'PriceEstimator';

export default PriceEstimator;
